import { computed, ComputedRef } from 'vue'
import CSEService from '@/services/CSEService'
import YoonlyService from '@/services/YoonlyService'

export function useDomainRedirect(): {
  handleRedirect: (
    targetUrl: string,
    hashParams?: Record<string, string>
  ) => Promise<void>
  isCSE: ComputedRef<boolean>
} {
  const cseService = new CSEService()

  const isCSE = computed(() => cseService.isCSE)

  /**
   * Rewrites Travelski URLs to use the current origin.
   * Intended to be called only when on Yoonly (caller gates with YoonlyService().isYoonly).
   *
   * @param url URL to rewrite (typically a Travelski domain).
   * @returns URL with current origin when url targets Travelski; otherwise the original url.
   */
  const rewriteUrlForYoonly = (url: string): string => {
    try {
      const urlObject = new URL(url)
      const travelskiDomains = [
        'www.travelski.com',
        'www.uk.travelski.com',
        'www.nl.travelski.com',
        'cse.travelski.com',
        // 'localhost' // Comment it in when running locally
      ]
      if (travelskiDomains.includes(urlObject.hostname)) {
        const currentOrigin = window.env.VUE_APP_MENU_DOMAIN ? window.env.VUE_APP_MENU_DOMAIN : window.location.hostname
        return `${currentOrigin}${urlObject.pathname}${urlObject.search}${urlObject.hash}`
      }
    } catch (error) {
      // If URL parsing fails, use as-is
      console.error('Failed to rewrite URL for Yoonly:', error)
    }
    return url
  }

  /**
   * Constructs a URL with hash parameters.
   * @param url Base URL.
   * @param params Object containing hash parameters.
   * @returns A URL with hash parameters appended.
   */
  const constructUrlWithHashParams = (
    url: string,
    params: Record<string, string>
  ): string => {
    const urlObject = new URL(url, window.location.origin)

    const hashParams = new URLSearchParams(urlObject.hash.slice(1))

    // Append the provided hash parameters
    Object.entries(params).forEach(([key, value]) => {
      hashParams.set(key, value)
    })

    // Set the new hash
    urlObject.hash = `#${hashParams.toString()}`

    return urlObject.toString()
  }

  const handleRedirect = async (
    targetUrl: string,
    hashParams?: Record<string, string>
  ): Promise<void> => {


    if (isCSE.value) {
      // If hashParams are provided, append them to the targetUrl
      const urlWithHash = hashParams
        ? constructUrlWithHashParams(targetUrl, hashParams)
        : targetUrl


      // Fetch the full redirect URL from CSEService
      const redirectUrl =
        await cseService.getRedirectUrlWithCredentials(urlWithHash)

      if (redirectUrl) {
        window.open(redirectUrl, '_blank')
      } else {
        console.error('Failed to construct redirect URL. Aborting redirect.')
      }
    } else {
      // Non-CSE redirection
      const urlWithHash = hashParams
        ? constructUrlWithHashParams(targetUrl, hashParams)
        : targetUrl

        const isYoonly = new YoonlyService().isYoonly
        if(isYoonly) {
          window.open(rewriteUrlForYoonly(urlWithHash), '_blank')
        } else {
          window.open(urlWithHash, '_blank')
        }

    }
  }

  return {
    handleRedirect,
    isCSE,
  }
}
